import { useContext, useEffect, useState } from "react";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../../../../context/appContext";
import Cookies from "universal-cookie";
import { useQuery } from "@tanstack/react-query";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { EditMFIUserWrapper } from "./styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseButton } from "../../../../components/button/styled";
import { retrieveAllMFIUsersService } from "../../../../utils/api/usermanagement/mfiuser/retrieveAllMFIUsers";
import { retrieveAllRolesService } from "../../../../utils/api/usermanagement/role/retrieveAllRoles";
import { retrieveAllBranchesService } from "../../../../utils/api/configuration/branch/retrieveAllBranches";
import { retrieveMFIUserService } from "../../../../utils/api/usermanagement/mfiuser/retrieveMFIUser";
import { updateMFIUserService } from "../../../../utils/api/usermanagement/mfiuser/updateMFIUser";
import { BaseAlertModal } from "../../../../components/modal/alert";
import uploadImageIcon from "../../../../assets/icons/uploadimageicon.svg";

export const EditMFIUser = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { id = "" } = useParams();
	const { setActiveNavItem } = useContext(AppContext);

	interface MFIUserFormDetailsType {
		firstName: string;
		middleName: string;
		lastName: string;
		email: string;
		avatar: File | string;
		supervisor: string;
		phone: string;
		role: string;
		branch: string;
		status: string;
		access: string;
	}

	useEffect(() => {
		setActiveNavItem("User Management");
	}, [setActiveNavItem]);

	const statuses = [
		{ label: "No Change", value: "active" },
		{ label: "Reset to Default", value: "inactive" },
	];
	const systemAccess = ["Regular", "HNI", "PEI"];

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [institutionId, setInstitutionId] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [formDetails, setFormDetails] = useState<MFIUserFormDetailsType>({
		firstName: "",
		middleName: "",
		lastName: "",
		email: "",
		supervisor: " ",
		phone: "",
		role: " ",
		branch: " ",
		status: " ",
		access: " ",
		avatar: "",
	});
	const [imagePreview, setImagePreview] = useState<string | null>(null);

	useEffect(() => {
		if (!id) return;
		retrieveMFIUserService(TOKEN, id)
			.then((data: Record<string, any>) => {
				setInstitutionId(data.institution);
				setFormDetails((prev: any) => ({
					...prev,
					...Object.keys(prev).reduce((acc: any, key) => {
						acc[key] = data[key] ?? prev[key];
						return acc;
					}, {}),
					supervisor: data?.supervisor ?? " ",
					role: data?.roles?.length ? data.roles[data.roles.length - 1] : "",
				}));
			})
			.catch((err) => {
				console.error("Failed to fetch MFI user:", err);
			});
	}, [TOKEN, id]);

	const { data: users } = useQuery({
		queryKey: ["users", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllMFIUsersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: roles } = useQuery({
		queryKey: ["roles", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllRolesService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: branches } = useQuery({
		queryKey: ["branchesForEditMFIUser", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllBranchesService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				if (e.target?.result) {
					setImagePreview(e.target.result as string);
					setFormDetails((prev) => ({
						...prev,
						avatar: file,
					}));
				}
			};
			reader.readAsDataURL(file);
		}
	};

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		if (!institutionId) return;
		const formData = new FormData();
		const data = {
			avatar: formDetails.avatar,
			firstName: formDetails.firstName,
			middleName: formDetails.middleName,
			lastName: formDetails.lastName,
			email: formDetails.email,
			phone: formDetails.phone,
			supervisor: formDetails.supervisor,
			role: formDetails.role,
			branch: formDetails.branch,
			status: formDetails.status,
			access: formDetails.access,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		try {
			const response = await updateMFIUserService(
				TOKEN,
				institutionId,
				id,
				formData,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to update MFI user. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`User update operation failed: ${error.message}`);
			console.error("User update operation failed:", error);
		}
	};

	return (
		<AppLayout pageId={"MFI-User"} pageTitle={"Edit MFI User"}>
			<EditMFIUserWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title="User Successfully Updated"
					message="Your information has been successfully updated."
					callToAction="Back to Users List"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required Information to add a branch
					</Typography>
				</Box>
				<form onSubmit={handleSubmit}>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">First Name</BaseLabel>
								<BaseInput
									required
									name="firstName"
									value={formDetails.firstName}
									placeholder="Enter your first name"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Middle Name</BaseLabel>
								<BaseInput
									// required
									name="middleName"
									value={formDetails.middleName}
									placeholder="Enter your middle name"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Last Name</BaseLabel>
								<BaseInput
									required
									name="lastName"
									value={formDetails.lastName}
									placeholder="Enter your last name"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Email</BaseLabel>
								<BaseInput
									required
									type="email"
									name="email"
									value={formDetails.email}
									placeholder="Enter your email"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Phone</BaseLabel>
								<BaseInput
									required
									name="phone"
									value={formDetails.phone}
									placeholder="Phone Number"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Supervisor</BaseLabel>
								<BaseSelect
									name="supervisor"
									fontweight={400}
									value={formDetails.supervisor}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Supervisor</BaseOption>
									{users?.map((user: Record<string, any>, index: number) => {
										return (
											<BaseOption key={index} value={user.id}>
												{`${user.firstName} ${user.lastName}`}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">User Role</BaseLabel>
								<BaseSelect
									required
									name="role"
									fontweight={400}
									value={formDetails.role}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select User Role</BaseOption>
									{roles?.map((role: Record<string, any>, index: number) => {
										return (
											<BaseOption key={index} value={role.id}>
												{role.title}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Branch</BaseLabel>
								<BaseSelect
									required
									name="branch"
									fontweight={400}
									value={formDetails.branch}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Branch</BaseOption>
									{branches?.map(
										(branch: Record<string, any>, index: number) => {
											return (
												<BaseOption key={index} value={branch.id}>
													{branch.name}
												</BaseOption>
											);
										},
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Password Action
								</BaseLabel>
								<BaseSelect
									required
									name="status"
									fontweight={400}
									value={formDetails.status}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Status</BaseOption>
									{statuses?.map(
										(
											status: { label: string; value: string },
											index: number,
										) => {
											return (
												<BaseOption key={index} value={status.value}>
													{status.label.charAt(0).toUpperCase() +
														status.label.slice(1).toLowerCase()}
												</BaseOption>
											);
										},
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Access</BaseLabel>
								<BaseSelect
									name="access"
									fontweight={400}
									value={formDetails.access}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Access</BaseOption>
									{systemAccess?.map((access: string, index: number) => {
										return (
											<BaseOption key={index} value={access}>
												{access}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Upload Profile Photo
								</BaseLabel>
								<Stack
									direction={"row"}
									alignItems={"center"}
									gap={{ mobile: "calc(var(--flex-gap)/4)" }}
								>
									{imagePreview ? (
										<img
											src={imagePreview}
											alt="user-avatar"
											className="user-avatar"
										/>
									) : (
										<img
											src={(formDetails?.avatar as string) || uploadImageIcon}
											alt="user-avatar"
											className="user-avatar"
										/>
									)}
									<BaseInput
										as={"input"}
										type="file"
										name="profilePhoto"
										sx={{ width: "100%" }}
										inputProps={{ accept: "image/jpeg, image/png" }}
										onChange={handleFileChange}
									/>
								</Stack>
							</BaseFieldSet>
						</Grid2>
					</Grid2>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box overflow={"hidden"}>
						<BaseButton
							type="submit"
							variant="contained"
							bgcolor="var(--primary-color)"
							disableElevation
							sx={{
								width: { mobile: "100%", miniTablet: "auto" },
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Submit
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
			</EditMFIUserWrapper>
		</AppLayout>
	);
};
